const SUPPORTED_ROUTE_TYPES = {
  mcq: 'mcq',
  quiz: 'quiz',
};

const LEVEL_FROM_CHAR = {
  e: 'level1',
  m: 'level2',
  h: 'level3',
};

export function parseTestingRoute(questionType, verified) {
  const apiQuestionType = SUPPORTED_ROUTE_TYPES[questionType];
  if (!apiQuestionType || !['0', '1'].includes(String(verified))) {
    return null;
  }

  return {
    apiQuestionType,
    verified: String(verified) === '1',
  };
}

/**
 * Parse hierarchy from question_id like qpy01mem01
 * (q + subtopic_id + type_char + level_char + m + nn).
 */
export function parseQuestionIdHierarchy(questionId) {
  const match = String(questionId || '').match(/^q(.+)([mcp])([emh])m(\d{2})$/i);
  if (!match) return null;

  const subTopicId = match[1];
  const levelChar = match[3].toLowerCase();
  return {
    subTopicId,
    topicId: subTopicId.length > 2 ? subTopicId.slice(0, -2) : subTopicId,
    subjectId: subTopicId.slice(0, 2),
    level: LEVEL_FROM_CHAR[levelChar] || null,
  };
}

function resolveHierarchy(record, data) {
  const fromId = parseQuestionIdHierarchy(record?.question_id);
  const metadata = data?.metadata || {};
  const subTopicId =
    record?.sub_topic_id ||
    metadata.subtopic_id ||
    metadata.sub_topic_id ||
    fromId?.subTopicId ||
    '';
  const topicId =
    record?.topic_id ||
    (subTopicId.length > 2 ? subTopicId.slice(0, -2) : fromId?.topicId) ||
    '';
  const level =
    record?.level ||
    metadata.level ||
    fromId?.level ||
    'level1';
  const subjectId =
    record?.subject_id || fromId?.subjectId || (subTopicId ? subTopicId.slice(0, 2) : '');

  return {
    subjectId,
    topicId,
    subTopicId,
    level: ['level1', 'level2', 'level3'].includes(String(level)) ? level : 'level1',
  };
}

function normalizeQuiz(record, data) {
  const content = data?.content;
  const answer = data?.answer;
  const options = content?.options;
  if (
    !content?.question ||
    !Array.isArray(options) ||
    options.length < 2 ||
    !Array.isArray(answer?.correct_option_ids)
  ) {
    return null;
  }

  const normalizedOptions = options
    .filter((option) => option?.id && typeof option?.text === 'string')
    .map((option) => ({ id: String(option.id), text: option.text }));
  if (normalizedOptions.length !== options.length) return null;

  return {
    id: String(record.question_id),
    text: content.question,
    options: normalizedOptions,
    correctOptionIds: answer.correct_option_ids.map(String),
    multiple: data?.metadata?.template === 'multiple_choice',
    explanation: answer.explanation || '',
    ...resolveHierarchy(record, data),
  };
}

function normalizeLegacyMcq(record, data) {
  if (!data?.question || !Array.isArray(data?.options) || data.options.length < 2) {
    return null;
  }

  const options = data.options.map((option, index) => ({
    id: `option_${index}`,
    text: typeof option === 'string' ? option : option?.text,
  }));
  if (options.some((option) => typeof option.text !== 'string')) return null;

  const correctAnswers = Array.isArray(data.correct_answer)
    ? data.correct_answer
    : [data.correct_answer];
  const correctOptionIds = options
    .filter((option) => correctAnswers.includes(option.text))
    .map((option) => option.id);
  if (!correctOptionIds.length) return null;

  return {
    id: String(record.question_id),
    text: data.question,
    options,
    correctOptionIds,
    multiple: correctOptionIds.length > 1,
    explanation: data.Explanation || data.explanation || '',
    ...resolveHierarchy(record, data),
  };
}

export function normalizeQuestion(record, apiQuestionType) {
  if (!record?.question_id || !record?.question_data) return null;
  return apiQuestionType === 'quiz'
    ? normalizeQuiz(record, record.question_data)
    : normalizeLegacyMcq(record, record.question_data);
}

export function isSelectionCorrect(selectedOptionIds, correctOptionIds) {
  if (!Array.isArray(selectedOptionIds) || !Array.isArray(correctOptionIds)) {
    return false;
  }
  const selected = [...new Set(selectedOptionIds)].sort();
  const correct = [...new Set(correctOptionIds)].sort();
  return (
    selected.length === correct.length &&
    selected.every((optionId, index) => optionId === correct[index])
  );
}

/**
 * Seed sessionStorage so /content-creation loads the selected MCQ/Quiz.
 */
export function prepareContentCreationNavigation({
  question,
  contentType,
  subjectId,
  subjectName = '',
  returnPath = '',
}) {
  if (!question?.id || !contentType || !subjectId) {
    throw new Error('Missing question, content type, or subject for navigation');
  }

  const topicId = question.topicId || '';
  const subTopicId = question.subTopicId || '';
  const level = question.level || 'level1';
  const resolvedSubjectName = subjectName || subjectId;

  sessionStorage.setItem('contentCreation_contentType', JSON.stringify(contentType));
  sessionStorage.setItem('contentCreation_selectedDifficulty', JSON.stringify(level));
  sessionStorage.setItem('contentCreation_selectedSubTopic', JSON.stringify(subTopicId));
  sessionStorage.setItem('contentCreation_selectedTopic', JSON.stringify(topicId));
  sessionStorage.setItem('contentCreation_selectedQuestion', JSON.stringify(String(question.id)));
  sessionStorage.setItem('contentCreation_subjectId', JSON.stringify(subjectId));
  sessionStorage.setItem('contentCreation_subjectName', JSON.stringify(resolvedSubjectName));
  sessionStorage.setItem('contentCreation_navSource', 'testing');
  if (returnPath) {
    sessionStorage.setItem('contentCreation_testingReturnPath', returnPath);
  }

  sessionStorage.removeItem('contentCreation_freezeFilters');
  sessionStorage.removeItem('contentCreation_projectId');
  sessionStorage.removeItem('contentCreation_projectName');

  return {
    subject_id: subjectId,
    subject_name: resolvedSubjectName,
    freezeFilters: false,
  };
}

/**
 * Build subtopic_id -> { topicId, topicName, subTopicId, subTopicName } from
 * topics_subtopics_by_subject response.
 */
export function buildTopicSubtopicLookup(topics = []) {
  const lookup = {};
  for (const topic of topics) {
    const topicId = topic?.topic_id;
    const topicName = topic?.topic_name || topicId || '';
    for (const subTopic of topic?.sub_topics || []) {
      const subTopicId = subTopic?.subtopic_id;
      if (!subTopicId) continue;
      lookup[subTopicId] = {
        topicId,
        topicName,
        subTopicId,
        subTopicName: subTopic.subtopic_name || subTopicId,
      };
    }
  }
  return lookup;
}

export function enrichQuestionWithNames(question, lookup = {}) {
  if (!question) return question;
  const match = question.subTopicId ? lookup[question.subTopicId] : null;
  if (!match) {
    return {
      ...question,
      topicName: question.topicName || question.topicId || '',
      subTopicName: question.subTopicName || question.subTopicId || '',
    };
  }
  return {
    ...question,
    topicId: match.topicId || question.topicId,
    topicName: match.topicName,
    subTopicId: match.subTopicId || question.subTopicId,
    subTopicName: match.subTopicName,
  };
}

function questionSearchHaystack(question) {
  const optionTexts = Array.isArray(question?.options)
    ? question.options.map((option) => option?.text || '')
    : [];
  return [
    question?.id,
    question?.text,
    question?.explanation,
    question?.topicName,
    question?.topicId,
    question?.subTopicName,
    question?.subTopicId,
    ...optionTexts,
  ]
    .filter(Boolean)
    .join('\n')
    .toLowerCase();
}

/**
 * Case-insensitive substring search across id, text, options, explanation,
 * topic, and subtopic.
 */
export function searchTestingQuestions(questions = [], query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];
  return questions.filter((question) => questionSearchHaystack(question).includes(q));
}
